import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import { PlusIcon } from "@icons";
import classNames from "classnames";
import React, { useCallback, useRef } from "react";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import styles from "./EditableCellContent.module.scss";
import { markEventBlocked } from "./ViewTable.lib";
import viewTableStyles from "./ViewTable.module.scss";

type Props = {
    children?: React.ReactNode;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    readonly?: boolean;
    required?: boolean;
    className?: string;
    classesClassName?: string;
    isEmpty?: boolean;
    truncationFullText?: string;
    tooltip?: React.ReactNode;
    size?: TriggerButtonProps["size"];
    addText?: string;
    hideIcon?: boolean;
    triggerProps?: React.ComponentPropsWithRef<"button">;
    ref?: React.Ref<HTMLButtonElement>;
} & Omit<
    React.ComponentPropsWithoutRef<"button">,
    "children" | "onClick" | "className" | "disabled" | "color" | "size"
>;

const EditableCellContentInner = ({
    children,
    onClick,
    readonly,
    required,
    className,
    classesClassName,
    isEmpty,
    truncationFullText,
    tooltip,
    size,
    addText,
    hideIcon,
    triggerProps,
    ref,
    ...rest
}: Props) => {
    const truncationRef = useRef<HTMLSpanElement>(null);
    const truncationTooltip = useTruncationTooltip({
        ref: truncationRef,
        isTruncated: isOverflowingX,
    });

    const { onClick: triggerOnClick, ...restTriggerProps } = triggerProps ?? {};

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            markEventBlocked(e);
            if (readonly) {
                e.stopPropagation();
                e.preventDefault();
                return;
            }
            onClick?.(e);
            triggerOnClick?.(e);
        },
        [onClick, triggerOnClick, readonly],
    );

    if (isEmpty) {
        return (
            <Tooltip content={tooltip}>
                <Button
                    ref={ref}
                    {...rest}
                    {...restTriggerProps}
                    variant="primary"
                    className={classNames(
                        viewTableStyles.cellAddButton,
                        styles.editableCellContent,
                        styles.addButton,
                        {
                            [styles.readonly]: readonly,
                            [styles.required]: required,
                        },
                        className,
                    )}
                    onClick={handleClick}
                    size={size ?? "md"}
                    leftIcon={hideIcon ? undefined : <PlusIcon />}
                >
                    {addText ?? "Add"}
                </Button>
            </Tooltip>
        );
    }

    return (
        <Tooltip content={tooltip}>
            <Button
                ref={ref}
                {...rest}
                {...restTriggerProps}
                variant="primary"
                onClick={handleClick}
                className={classNames(
                    styles.editableCellContent,
                    {
                        [styles.readonly]: readonly,
                    },
                    className,
                    classesClassName,
                )}
                disabled={readonly}
                size={size ?? "md"}
            >
                {truncationFullText && !tooltip ? (
                    <Tooltip
                        content={truncationFullText}
                        {...truncationTooltip}
                    >
                        <span
                            className={styles.truncationContainer}
                            ref={truncationRef}
                        >
                            {children}
                        </span>
                    </Tooltip>
                ) : (
                    children
                )}
            </Button>
        </Tooltip>
    );
};

export const EditableCellContent = React.memo(EditableCellContentInner);
EditableCellContent.displayName = "EditableCellContent";
